// stores/dashboardStorage.ts — quota-hardened localStorage engine for dashboardStore.
//
// Zustand persist serializes the partialized state to JSON and calls
// storage.setItem(key, value). On a full origin (≈5 MB) that throws a
// QuotaExceededError, which would otherwise bubble out of set() and leave the
// dashboard in a half-saved / white-screen state (§9.2 / §11 risk 6).
//
// This adapter intercepts setItem: on a quota error it lazily evicts the OLDEST
// user custom layout (isPreset:false, lowest createdAt) — never a preset (presets
// aren't persisted anyway) and never the currently-active layout if any other
// custom exists to evict first — then retries. It repeats until the write
// succeeds or there is nothing left to evict, surfacing a single non-fatal notice
// per eviction via dashboardNoticeStore. Data loss is bounded to old, unused
// customs; the active layout and in-memory state are preserved.
//
// createJSONStorage wraps this, so getItem/setItem/removeItem speak raw strings.

import { createJSONStorage, type StateStorage } from 'zustand/middleware';
import { pushDashboardNotice } from './dashboardNoticeStore';

/** Loose shape of the persisted blob (partialize output): activeLayout + customs. */
interface PersistedBlob {
  state?: {
    activeLayout?: unknown;
    layouts?: Record<string, { isPreset?: unknown; createdAt?: unknown; name?: unknown }>;
  };
  version?: unknown;
}

/**
 * Resolve localStorage defensively. Returns null when there is no DOM (SSR, the
 * node-env unit suite, or a browser with storage disabled) so callers degrade to a
 * non-fatal no-op instead of throwing a ReferenceError out of Zustand's set().
 */
function getLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage ?? null;
  } catch {
    // Accessing localStorage can itself throw (e.g. sandboxed iframe, privacy mode).
    return null;
  }
}

function isQuotaError(err: unknown): boolean {
  if (!(err instanceof DOMException)) return false;
  // Cover the spec name, the legacy Firefox name, and the legacy numeric codes.
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    err.code === 22 ||
    err.code === 1014
  );
}

/**
 * Pick the eviction victim: the oldest custom layout, preferring one that is NOT
 * the active layout. Returns the layout id, or null if nothing is evictable.
 */
function pickVictim(blob: PersistedBlob): string | null {
  const layouts = blob.state?.layouts;
  if (!layouts || typeof layouts !== 'object') return null;
  const active =
    typeof blob.state?.activeLayout === 'string' ? blob.state.activeLayout : null;

  const customs = Object.entries(layouts)
    .filter(([, l]) => l && l.isPreset === false)
    .map(([id, l]) => ({
      id,
      createdAt: typeof l.createdAt === 'number' ? l.createdAt : 0,
      name: typeof l.name === 'string' ? l.name : id,
    }))
    .sort((a, b) => a.createdAt - b.createdAt);

  if (customs.length === 0) return null;
  // Prefer the oldest non-active custom; only sacrifice the active one if it is
  // the sole remaining custom.
  const nonActive = customs.find((c) => c.id !== active);
  return (nonActive ?? customs[0])!.id;
}

/** Re-serialize the blob with one custom layout removed. */
function evict(blob: PersistedBlob, victimId: string): PersistedBlob {
  const layouts = { ...(blob.state?.layouts ?? {}) };
  delete layouts[victimId];
  return { ...blob, state: { ...blob.state, layouts } };
}

/**
 * Quota-aware setItem. Attempts the write; on a quota error, parses the value,
 * evicts the oldest custom, and retries — bounded by the number of customs.
 */
function safeSetItem(key: string, value: string): void {
  const ls = getLocalStorage();
  if (!ls) return; // no DOM (SSR / node-env tests): persist is a non-fatal no-op.

  try {
    ls.setItem(key, value);
    return;
  } catch (err) {
    if (!isQuotaError(err)) throw err; // non-quota errors are real bugs — surface them.
  }

  let blob: PersistedBlob;
  try {
    blob = JSON.parse(value) as PersistedBlob;
  } catch {
    // Value isn't our JSON shape; nothing to evict — give up quietly.
    console.error('[dashboardStorage] quota hit on unparseable value; skipping persist');
    return;
  }

  let current = blob;
  const evictedNames: string[] = [];
  // Bound the loop by the live custom count to guarantee termination.
  for (let guard = 0; guard < 64; guard += 1) {
    const victim = pickVictim(current);
    if (!victim) break;
    const victimName = current.state?.layouts?.[victim]?.name;
    evictedNames.push(typeof victimName === 'string' ? victimName : victim);
    current = evict(current, victim);
    try {
      ls.setItem(key, JSON.stringify(current));
      pushDashboardNotice(
        'warn',
        `Storage full — evicted ${evictedNames.length} old custom layout(s) to save your dashboard: ${evictedNames.join(', ')}.`,
      );
      return;
    } catch (retryErr) {
      if (!isQuotaError(retryErr)) throw retryErr;
      // still over quota → loop and evict the next-oldest custom.
    }
  }

  // Exhausted evictable customs and still over quota: keep in-memory state intact,
  // warn the user, but do NOT throw (a throw would propagate into set()).
  console.error('[dashboardStorage] quota exceeded; could not persist dashboard');
  pushDashboardNotice(
    'warn',
    'Browser storage is full — your dashboard changes are kept for this session but could not be saved.',
  );
}

/** The StateStorage Zustand will call; getItem/removeItem pass through. */
const quotaGuardedStorage: StateStorage = {
  getItem: (key) => {
    try {
      return getLocalStorage()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  setItem: (key, value) => safeSetItem(key, value),
  removeItem: (key) => {
    try {
      getLocalStorage()?.removeItem(key);
    } catch {
      /* non-fatal */
    }
  },
};

/** The persist `storage` option for dashboardStore. */
export const dashboardJSONStorage = createJSONStorage(() => quotaGuardedStorage);
